import { useMemo, useState } from "react";
import numeral from "numeral";
import moment from "moment";

export const useDualYAxisChart = ({
  data,
  yAxisInfos,
  selectedYAxis,
  colors,
}) => {
  const [showSplitLine, setShowSplitLine] = useState(false);

  const { yAxisData, xAxisData } = data;
  const { lineChartData, barChartData } = yAxisData;

  // Filtered YAxisInfos
  const filteredYAxisInfos = useMemo(
    () =>
      yAxisInfos
        .filter((axisInfo) => selectedYAxis.includes(axisInfo.key))
        .sort(
          (a, b) => selectedYAxis.indexOf(a.key) - selectedYAxis.indexOf(b.key)
        ),
    [yAxisInfos, selectedYAxis]
  );

  // Series data for Line and Bar charts
  const filteredLineSeries = useMemo(() => {
    if (!lineChartData) return [];
    return filteredYAxisInfos.map((axisInfo, index) => ({
      name: axisInfo.name,
      type: "line",
      data: lineChartData.map((d) => d[axisInfo.key]),
      symbolSize: 5,
      itemStyle: {
        color: colors[index],
        borderWidth: 1, // have bug
      },
      yAxisIndex: index,
    }));
  }, [filteredYAxisInfos, lineChartData]);

  const filteredBarSeries = useMemo(() => {
    if (!barChartData) return [];
    return filteredYAxisInfos.map((axisInfo, index) => ({
      name: axisInfo.name,
      type: "bar",
      data: barChartData.map((d) => ({
        value: d[axisInfo.key],
        itemStyle: {
          borderRadius: d[axisInfo.key] > 0 ? [3, 3, 0, 0] : [0, 0, 3, 3],
          color: d[axisInfo.key] > 0 ? colors[index] : colors[index] + "80", // opicity 50% while negative
        },
      })),
      barMaxWidth: 20,
      showSymbol: false,
      large: true, // big data mode
      largeThreshold: 2000, // over 2000 data points switch to performance mode
      yAxisIndex: index,
    }));
  }, [filteredYAxisInfos, barChartData, colors]);

  // Calculate Y-axis min and max values
  const [leftMin, leftMax, rightMin, rightMax] = useMemo(() => {
    if (!barChartData) return [undefined, undefined, undefined, undefined];

    const leftValues = barChartData.map(
      (d) => d[filteredYAxisInfos[0]?.key] || 0
    );
    const rightValues = barChartData.map(
      (d) => d[filteredYAxisInfos[1]?.key] || 0
    );

    let [leftMin, leftMax] = [Math.min(...leftValues), Math.max(...leftValues)];
    let [rightMin, rightMax] = [
      Math.min(...rightValues),
      Math.max(...rightValues),
    ];
    const isLeftAll0 = leftMin === 0 && leftMax === 0;
    const isRightAll0 = rightMin === 0 && rightMax === 0;

    const leftPositiveValue = leftMax > 0 ? leftMax : 0;
    const leftNegativeValue = leftMin < 0 ? Math.abs(leftMin) : 0;
    const rightPositiveValue = rightMax > 0 ? rightMax : 0;
    const rightNegativeValue = rightMin < 0 ? Math.abs(rightMin) : 0;

    // left situation: allP || p+n || allN || 0
    // right situation: allP || p+n || allN || 0
    // All positive values or all negative values, no need to adjust the scale
    if (isLeftAll0 || isRightAll0) {
      setShowSplitLine(false);
      if (leftPositiveValue === 0 && !isLeftAll0) {
        // left allN + right 0
        rightMin = leftMin;
      } else if (rightPositiveValue === 0 && !isRightAll0) {
        // left 0 + right allP
        leftMin = rightMin;
      } else {
        // left allP + right 0 || left 0 + right allP || left 0 + right 0 || left p+n + right 0 || left 0 + right p+n
        leftMin = undefined;
        rightMin = undefined;

        setShowSplitLine(true);
      }

      leftMax = undefined;
      rightMax = undefined;
    } else {
      setShowSplitLine(true);

      if (leftPositiveValue === 0 && rightPositiveValue === 0) {
        // left allN + right allN
        leftMin = undefined;
        rightMin = undefined;
        leftMax = undefined;
        rightMax = undefined;
      } else if (leftNegativeValue === 0 && rightNegativeValue === 0) {
        // left allP + right allP
        leftMin = undefined;
        rightMin = undefined;
        leftMax = undefined;
        rightMax = undefined;
      } else {
        setShowSplitLine(false);
        if (leftNegativeValue === 0 && rightPositiveValue === 0) {
          // left allP + right allN
          leftMin = -leftMax;
          rightMax = -rightMin;
        } else if (rightNegativeValue === 0 && leftPositiveValue === 0) {
          // left allN + right allP
          rightMin = -rightMax;
          leftMax = -leftMin;
        } else if (leftPositiveValue === 0) {
          // left allN + right p+n
          leftMax =
            (leftNegativeValue * rightPositiveValue) / rightNegativeValue;
        } else if (rightPositiveValue === 0) {
          // left p+n + right allN
          rightMax =
            (rightNegativeValue * leftPositiveValue) / leftNegativeValue;
        } else {
          // left p+n + right p+n || left allP + right p+n || left p+n + right allP
          const leftRatio = leftNegativeValue / leftPositiveValue;
          const rightRatio = rightNegativeValue / rightPositiveValue;

          if (leftRatio > rightRatio) {
            rightMin = -(
              (leftNegativeValue * rightPositiveValue) /
              leftPositiveValue
            );
          } else if (rightRatio > leftRatio) {
            leftMin = -(
              (rightNegativeValue * leftPositiveValue) /
              rightPositiveValue
            );
          }
        }

        // Add 15% space to both sides
        const leftRange = leftMax - leftMin;
        const rightRange = rightMax - rightMin;
        leftMax = leftMax + leftRange * 0.15;
        leftMin = leftMin - leftRange * 0.15;
        rightMax = rightMax + rightRange * 0.15;
        rightMin = rightMin - rightRange * 0.15;
      }
    }

    return [leftMin, leftMax, rightMin, rightMax];
  }, [barChartData, filteredYAxisInfos]);

  // Chart options
  const getChartOptions = (
    chartType,
    otherCustomSetting,
    hasCustomLegend = false
  ) => {
    const formatNumberWithSymbol = (value) => {
      const formatted = numeral(value).format(`0,0.0a`).toUpperCase();
      const hasSymbol = isNaN(Number(formatted));
      const formattedNumber = hasSymbol
        ? Number(formatted.slice(0, -1))
        : Number(formatted);
      const symbol = formatted.slice(-1);
      return hasSymbol ? `${formattedNumber}${symbol}` : `${formattedNumber}`;
    };

    // Common chart options
    const commonOptions = {
      animation: false,
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
          crossStyle: {
            color: "#A5A5A5",
          },
          // Label at axis while showing tooltip
          label: {
            fontSize: 8,
            formatter: (params) => {
              const { value, axisDimension } = params;
              if (axisDimension === "x") {
                if (otherCustomSetting.formatXAxisLabel.open)
                  return moment(value).format(
                    otherCustomSetting.formatXAxisLabel.format
                  ); // hide year;
                return value;
              }

              return formatNumberWithSymbol(value);
            },
          },
        },
        position: (point) => ({ left: point[0] + 10, top: point[1] + 30 }), // Fix tooltip to the right
      },
      legend: {
        show: !hasCustomLegend,
        top: 0,
        left: 0,
        icon: "roundRect",
        itemWidth: 12,
        itemHeight: 8,
        data: filteredYAxisInfos.map((total) => total.name),
      },
      grid: {
        top: hasCustomLegend ? 30 : 55,
        bottom: hasCustomLegend ? 20 : 5,
        left: 15,
        right: 15,
        containLabel: true,
      },
      color: colors.slice(0, filteredYAxisInfos.length),
      xAxis: {
        type: "category",
        data: xAxisData,
        // X-axis style
        axisLine: {
          lineStyle: {
            color: "#A5A5A5",
          },
        },
        axisLabel: {
          color: "#A2A3A5",
          fontSize: 8,
          margin: 10,
          formatter: function (value) {
            if (otherCustomSetting.formatXAxisLabel.open)
              return moment(value).format(
                otherCustomSetting.formatXAxisLabel.format
              ); // hide year;
            return value;
          },
        },
      },
    };

    // Common Y-axis options
    const commonYAxisOptions = (yAxisIndex) => ({
      type: "value",
      // Y-axis style
      axisLine: {
        show: true,
        lineStyle: {
          color: colors.slice(0, filteredYAxisInfos.length)[yAxisIndex],
        },
      },
      axisLabel: {
        formatter: formatNumberWithSymbol,
        fontSize: 8,
      },
    });

    // Specific Y-axis options
    const specificYAxisOptions = (yAxisIndex, chartType) => {
      const [min, max] = [
        yAxisIndex === 0 ? leftMin : rightMin,
        yAxisIndex === 0 ? leftMax : rightMax,
      ];

      const specificConfig = {
        line: {
          alignTicks: true,
        },
        bar: {
          max,
          min,
          axisLabel: {
            showMaxLabel: showSplitLine,
            showMinLabel: showSplitLine,
          },
          splitLine: {
            show: showSplitLine,
          },
          alignTicks: showSplitLine,
        },
      };

      return specificConfig[chartType] || {};
    };

    // Y-axis options
    const yAxisOptions = (chartType) => {
      if (filteredYAxisInfos.length === 0) {
        setShowSplitLine(true);
        return [commonYAxisOptions(0)];
      }

      const yAxisArray = filteredYAxisInfos.map((_, index) => ({
        ...commonYAxisOptions(index),
        ...specificYAxisOptions(index, chartType),
        // avoid overlap of axis labels
        axisLabel: {
          ...commonYAxisOptions(index).axisLabel,
          ...specificYAxisOptions(index, chartType).axisLabel,
        },
      }));
      setShowSplitLine(false);
      return yAxisArray;
    };

    // Specific chart options for each type
    const specificOptions = {
      line: {
        xAxis: {
          boundaryGap: false,
        },
        series: filteredLineSeries,
      },
      bar: {
        series: filteredBarSeries,
      },
    };

    // Final chart options
    const typeSpecificOptions = specificOptions[chartType] || {};
    return {
      ...commonOptions,
      ...typeSpecificOptions,
      yAxis: yAxisOptions(chartType),
      // avoid overlap of axis labels
      xAxis: {
        ...commonOptions.xAxis,
        ...typeSpecificOptions.xAxis,
      },
    };
  };

  return { getChartOptions };
};
